import { forwardRef, type TextareaHTMLAttributes, useState } from 'react';
import { cn } from '../../utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean;
  /** Optional max length — when set, a "N / max" counter is rendered. */
  readonly maxLength?: number;
  readonly showCounter?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, maxLength, showCounter, className, disabled, onChange, defaultValue, value, ...rest },
  ref,
) {
  const [count, setCount] = useState(() => String(value ?? defaultValue ?? '').length);
  return (
    <div className={cn('inline-flex w-full flex-col gap-1', disabled && 'opacity-45')}>
      <textarea
        ref={ref}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={invalid || undefined}
        defaultValue={defaultValue}
        value={value}
        onChange={(e) => {
          setCount(e.target.value.length);
          onChange?.(e);
        }}
        className={cn(
          'w-full min-w-0 min-h-24 rounded-lg bg-surface text-ink p-3 text-body [@media(pointer:coarse)]:text-base',
          'border border-ink-3/80 placeholder:text-ink-3 resize-y',
          'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
          'focus:outline-2 focus:outline-offset-2 focus:outline-accent-text focus:border-accent-text',
          'disabled:cursor-not-allowed',
          invalid && 'border-danger focus:border-danger focus:ring-danger/25',
          className,
        )}
        {...rest}
      />
      {maxLength && showCounter ? (
        <div className="self-end text-[11px] text-ink-3 font-mono">
          {value !== undefined ? String(value).length : count} / {maxLength}
        </div>
      ) : null}
    </div>
  );
});
